/**
 * HTTP contract between the course Worker and the browser.
 *
 * Every JSON endpoint under /api (except /api/auth/*, which is better-auth's
 * own protocol) answers with the envelope below. Routes:
 *
 *   GET    /api/courses                              → CourseSummary[]   (published only)
 *   GET    /api/courses/:slug                        → CourseDetail      (published only)
 *   GET    /api/courses/:slug/lessons/:lessonSlug    → LessonView        (published only)
 *   GET    /api/progress/:courseSlug                 → Progress          (signed in)
 *   PUT    /api/progress/lessons/:lessonId           → Progress          (signed in; SetProgressInput;
 *                                                       progress of that lesson's course)
 *
 *   GET    /api/admin/courses                        → CourseSummary[]   (admin; drafts too)
 *   POST   /api/admin/courses                        → CourseDetail      (admin; CreateCourseInput;
 *                                                       new course is a draft)
 *   GET    /api/admin/courses/:id                    → CourseDetail      (admin)
 *   PUT    /api/admin/courses/:id                    → CourseDetail      (admin; UpdateCourseInput;
 *                                                       409 STALE on old expectedUpdatedAt)
 *   DELETE /api/admin/courses/:id                    → null              (admin; cascades lessons +
 *                                                       progress)
 *   POST   /api/admin/courses/:id/lessons            → Lesson            (admin; CreateLessonInput;
 *                                                       appended last)
 *   PUT    /api/admin/courses/:id/lessons/order      → CourseDetail      (admin; ReorderLessonsInput;
 *                                                       must list every lesson of the course)
 *   GET    /api/admin/lessons/:id                    → Lesson            (admin)
 *   PUT    /api/admin/lessons/:id                    → Lesson            (admin; UpdateLessonInput;
 *                                                       409 STALE on old expectedUpdatedAt)
 *   DELETE /api/admin/lessons/:id                    → null              (admin; remaining lessons
 *                                                       renumbered)
 *   POST   /api/admin/media                          → { url }           (admin; multipart field
 *                                                       "file"; MEDIA_TYPES, ≤ MEDIA_MAX_BYTES)
 *   GET    /api/admin/roles?q=&role=&page=           → AccountPage       (user manager; AccountQuery:
 *                                                       every account, ACCOUNT_PAGE_SIZE per page,
 *                                                       newest first; see Roles)
 *   PUT    /api/admin/roles/:id                      → RoleChange        (user manager; SetRoleInput;
 *                                                       see Roles)
 *
 *   GET    /media/:key                               → stored upload (public, immutable cache)
 *
 * A role name means that role or higher: owner ⊇ admin ⊇ analyst ⊇ learner (ROLES in core.ts).
 * Roles: a site uses some of ROLES (course sites: COURSE_SITE_ROLES). Its user manager is
 * managerRole(siteRoles) (research: admin; course sites: owner); below it both routes answer 403
 * FORBIDDEN. GET lists every account with its role on the site (a stored role the site does not
 * use reads as learner) and counts every account per role; `total` counts the accounts matching q
 * (name or e-mail contains it) and role. PUT gives the account with :id the role only when its
 * current role and the new role both rank below the caller's; owners and the caller's own account
 * never change here (403 FORBIDDEN). A bad query or body, "owner", or a role the site does not use
 * → 400 VALIDATION; an unknown id → 404 NOT_FOUND. The answer is the account with its new role
 * and the role it had. Owners are made and removed only with the `role:set` CLI. Sibling Workers
 * on the kit's auth tables (research) mount the same routes.
 *
 * Slug clashes answer 409 CONFLICT. Validation failures answer 400 VALIDATION
 * with the first zod issue as message, prefixed with its path ("lessons.0.slug: …").
 * JSON bodies over 1 MB answer 413 TOO_LARGE; too many writes (non-GET) by one
 * signed-in user in a short time answer 429 RATE_LIMITED.
 */
import type {
  AccountPage,
  AccountQuery,
  CourseDetail,
  CourseSummary,
  CreateCourseInput,
  CreateLessonInput,
  Lesson,
  LessonView,
  Progress,
  ReorderLessonsInput,
  RoleChange,
  SetProgressInput,
  SetRoleInput,
  UpdateCourseInput,
  UpdateLessonInput,
} from "./index.ts";

export const ERROR_CODES = [
  "VALIDATION",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "STALE",
  "TOO_LARGE",
  "RATE_LIMITED",
  "INTERNAL",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiSuccess<T> {
  ok: true;
  data: T;
}

export interface ApiFailure {
  ok: false;
  error: { message: string; code: ErrorCode };
}

export type ApiEnvelope<T> = ApiSuccess<T> | ApiFailure;

/** Typed browser client for the routes above (see createApiClient). */
export interface CourseKitApi {
  listCourses(): Promise<CourseSummary[]>;
  getCourse(slug: string): Promise<CourseDetail>;
  getLesson(courseSlug: string, lessonSlug: string): Promise<LessonView>;
  getProgress(courseSlug: string): Promise<Progress>;
  setLessonProgress(lessonId: string, input: SetProgressInput): Promise<Progress>;
  admin: {
    listCourses(): Promise<CourseSummary[]>;
    createCourse(input: CreateCourseInput): Promise<CourseDetail>;
    getCourse(id: string): Promise<CourseDetail>;
    updateCourse(id: string, input: UpdateCourseInput): Promise<CourseDetail>;
    deleteCourse(id: string): Promise<void>;
    createLesson(courseId: string, input: CreateLessonInput): Promise<Lesson>;
    reorderLessons(courseId: string, input: ReorderLessonsInput): Promise<CourseDetail>;
    getLesson(id: string): Promise<Lesson>;
    updateLesson(id: string, input: UpdateLessonInput): Promise<Lesson>;
    deleteLesson(id: string): Promise<void>;
    uploadMedia(file: Blob, filename: string): Promise<{ url: string }>;
    listAccounts(query: AccountQuery): Promise<AccountPage>;
    setRole(userId: string, input: SetRoleInput): Promise<RoleChange>;
  };
}
